import MailInfo from '@/taskpane/events/models/mail-info'
import { officeApiMailbox_GetEwsCallbackToken, officeApiMailbox_MakeEwsRequestAsync } from './async/office-api-mailbox-async'
import configuration from '../common/configuration'
import { EwsEmailListItem } from '../events/models/email-list-item'

export function isEwsApiAvailable(): boolean {
  const ewsUrl = Office.context.mailbox.ewsUrl

  if (ewsUrl) {
    const officeEwsUrl = 'https://outlook.office.com/api'
    const office365EwsUrl = 'https://outlook.office365.com/api'

    const isExchangeOnline = ewsUrl.toLowerCase().startsWith(officeEwsUrl) || ewsUrl.toLowerCase().startsWith(office365EwsUrl)
    return !isExchangeOnline
  } else {
    return false
  }
}

export function officeMailEwsUrl(): string {
  return Office.context.mailbox.ewsUrl
}

export async function officeMailEwsInfo(id: string): Promise<MailInfo> {
  const request =
    '<soap:Envelope' +
    '    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"' +
    '    xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages"' +
    '    xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"' +
    '    xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">' +
    '   <soap:Header>' +
    '   <t:RequestServerVersion Version="Exchange2013" />' +
    '    </soap:Header>' +
    '    <soap:Body>' +
    '    <m:GetItem>' +
    '        <m:ItemShape>' +
    '        <t:BaseShape>AllProperties</t:BaseShape>' +
    '        </m:ItemShape>' +
    '        <m:ItemIds>' +
    // eslint-disable-next-line prettier/prettier
    '        <t:ItemId Id="' + id + '" />' +
    '        </m:ItemIds>' +
    '    </m:GetItem>' +
    '    </soap:Body>' +
    '</soap:Envelope>'

  const ewsResponse = await ewsAsyncMethodWithRetry(() => officeApiMailbox_MakeEwsRequestAsync(request))
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(ewsResponse, 'text/html')

  const ewsDateTimeSent = getEwsResponseValue(xmlDoc, 't:DateTimeSent')

  const ewsData = {
    bcc: getEwsResponseRecipientList(xmlDoc, 't:BccRecipients'),
    datetime: ewsDateTimeSent ? new Date(ewsDateTimeSent) : null,
    threadId: getEwsResponseValue(xmlDoc, 't:ConversationId', 'Id'),
    threadIndex: getEwsResponseValue(xmlDoc, 't:ConversationIndex') || '',
    headerMessageId: getEwsResponseValue(xmlDoc, 't:internetMessageId'),
  } as MailInfo

  if (ewsData.headerMessageId) {
    ewsData.headerMessageId = ewsData.headerMessageId.replace('&lt;', '<').replace('&gt;', '>')
  }

  return ewsData
}

async function getSearchFolderId() {
  const appId = configuration.MANIFEST_APP_ID
  const folderName = 'Cirrus Insight Process queue 1' + appId

  const findFolderRequest =
    '<soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">' +
    '  <soap:Header>' +
    '    <RequestServerVersion Version="Exchange2013" soap:mustUnderstand="0" xmlns="http://schemas.microsoft.com/exchange/services/2006/types"/>' +
    '  </soap:Header>' +
    '  <soap:Body>' +
    '    <FindFolder Traversal="Shallow" xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">' +
    '      <FolderShape>' +
    '        <t:BaseShape>Default</t:BaseShape>' +
    '      </FolderShape>' +
    '      <Restriction>' +
    '        <t:IsEqualTo>' +
    '          <t:FieldURI FieldURI="folder:DisplayName"/>' +
    '          <t:FieldURIOrConstant>' +
    `            <t:Constant Value="${folderName}"/>` +
    '          </t:FieldURIOrConstant>' +
    '        </t:IsEqualTo>' +
    '      </Restriction>' +
    '      <ParentFolderIds>' +
    '        <t:DistinguishedFolderId Id="root"/>' +
    '      </ParentFolderIds>' +
    '    </FindFolder>' +
    '  </soap:Body>' +
    '</soap:Envelope>'

  const createFolderRequest =
    '<soap:Envelope xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages"' +
    '  xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"' +
    '  xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"' +
    '  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">' +
    '  <soap:Header>' +
    '    <RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0" />' +
    '  </soap:Header>' +
    '  <soap:Body>' +
    '    <m:CreateFolder>' +
    '      <m:ParentFolderId>' +
    '        <t:DistinguishedFolderId Id="root"/>' +
    '      </m:ParentFolderId>' +
    '      <m:Folders>' +
    '        <t:SearchFolder>' +
    `          <t:DisplayName>${folderName}</t:DisplayName>` +
    '          <t:SearchParameters Traversal="Shallow">' +
    '            <t:Restriction>' +
    '              <t:And>' +
    '                <t:Exists>' +
    `                  <t:ExtendedFieldURI DistinguishedPropertySetId="PublicStrings" PropertyName="cecp-${appId}" PropertyType="String"/>` +
    '                </t:Exists>' +
    '                <t:Not>' +
    '                  <t:Exists>' +
    `                    <t:ExtendedFieldURI PropertyName="CIHandled" PropertySetId="${appId}" PropertyType="Boolean"/>` +
    '                  </t:Exists>' +
    '                </t:Not>' +
    '              </t:And>' +
    '            </t:Restriction>' +
    '            <t:BaseFolderIds>' +
    '              <t:DistinguishedFolderId Id="sentitems"/>' +
    '            </t:BaseFolderIds>' +
    '          </t:SearchParameters>' +
    '        </t:SearchFolder>' +
    '      </m:Folders>' +
    '    </m:CreateFolder>' +
    '  </soap:Body>' +
    '</soap:Envelope>'

  const folderListEwsResponse = await ewsAsyncMethodWithRetry(() => officeApiMailbox_MakeEwsRequestAsync(findFolderRequest))

  const parser = new DOMParser()
  const folderListXml = parser.parseFromString(folderListEwsResponse, 'text/xml')

  const folderList = folderListXml.getElementsByTagName('t:Folders')[0].childNodes

  if (folderList.length > 0) {
    return getEwsResponseValue(folderListXml, 't:FolderId', 'Id')
  } else {
    const createFolderResponse = await ewsAsyncMethodWithRetry(() => officeApiMailbox_MakeEwsRequestAsync(createFolderRequest))
    const createFolderXml = parser.parseFromString(createFolderResponse, 'text/xml')
    return getEwsResponseValue(createFolderXml, 't:FolderId', 'Id')
  }
}

async function getSentItems(lastProcessedDateTime: Date) {
  const folderId = await getSearchFolderId()
  const appId = configuration.MANIFEST_APP_ID

  // Format date for EWS (ISO 8601 format)
  const dateRestriction = lastProcessedDateTime
    ? '      <Restriction>' +
      '        <t:GreaterThanOrEqual>' +
      '          <t:FieldURI FieldURI="item:DateTimeSent"/>' +
      '          <t:FieldURIOrConstant>' +
      `            <t:Constant Value="${lastProcessedDateTime.toISOString()}"/>` +
      '          </t:FieldURIOrConstant>' +
      '        </t:GreaterThanOrEqual>' +
      '      </Restriction>\n' +
      ''
    : ''

  const findItemRequest =
    '<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"' +
    '         xmlns:xsd="http://www.w3.org/2001/XMLSchema"' +
    '         xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"' +
    '         xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">' +
    '  <soap:Header>' +
    '    <RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0" />' +
    '  </soap:Header>' +
    '  <soap:Body>' +
    '    <FindItem Traversal="Shallow" xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">' +
    '      <ItemShape>' +
    '        <t:BaseShape>IdOnly</t:BaseShape>' +
    '        <t:AdditionalProperties>' +
    '          <t:FieldURI FieldURI="message:InternetMessageId" />' +
    '          <t:FieldURI FieldURI="item:ConversationId" />' +
    '          <t:FieldURI FieldURI="message:ConversationIndex" />' +
    '          <t:FieldURI FieldURI="item:DateTimeSent" />' +
    `          <t:ExtendedFieldURI PropertyName="cecp-${appId}" DistinguishedPropertySetId="PublicStrings"  PropertyType="String" />` +
    `          <t:ExtendedFieldURI PropertySetId="${appId}" PropertyName="CIHandled" PropertyType="Boolean" />` +
    '        </t:AdditionalProperties>' +
    '      </ItemShape>' +
    dateRestriction +
    '      <ParentFolderIds>' +
    `        <t:FolderId Id="${folderId}"/>` +
    '      </ParentFolderIds>' +
    '    </FindItem>' +
    '  </soap:Body>' +
    '</soap:Envelope>'

  const ewsResponse = await ewsAsyncMethodWithRetry(() => officeApiMailbox_MakeEwsRequestAsync(findItemRequest))

  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(ewsResponse, 'text/xml')

  const emailsToProcess: EwsEmailListItem[] = []
  const items = xmlDoc.getElementsByTagName('t:Items')[0].children

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.nodeName.toLowerCase() === 't:message') {
      const emailItem: EwsEmailListItem = {} as EwsEmailListItem
      for (let j = 0; j < item.children.length; j++) {
        const itemProp = item.children[j]
        switch (itemProp.nodeName.toLowerCase()) {
          case 't:itemid':
            emailItem.id = itemProp.attributes['Id'].value
            emailItem.changeKey = itemProp.attributes['ChangeKey'].value
            break
          case 't:conversationid':
            emailItem.threadId = itemProp.attributes['Id'].value
            break
          case 't:conversationindex':
            emailItem.threadIndex = itemProp.textContent || ''
            break
          case 't:internetmessageid':
            emailItem.headerMessageId = itemProp.textContent
            break
          case 't:datetimesent':
            emailItem.sentTime = new Date(itemProp.textContent)
            break
          case 't:extendedproperty':
            try {
              emailItem.extendedProperties = JSON.parse(itemProp.textContent)
            } catch (e) {
              console.error('Error parsing extended properties. Content found:', itemProp.textContent)
            }
            break
        }
      }

      emailsToProcess.push(emailItem)
    }
  }
  return emailsToProcess
}

async function markItemAsProcessed(id: string, changeKey: string) {
  const appId = configuration.MANIFEST_APP_ID
  const xmlRequest =
    '<?xml version="1.0" encoding="utf-8"?>' +
    '<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"' +
    '         xmlns:xsd="http://www.w3.org/2001/XMLSchema"' +
    '         xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"' +
    '         xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">' +
    '  <soap:Header>' +
    '    <RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0" />' +
    '  </soap:Header>' +
    '  <soap:Body>' +
    '    <UpdateItem MessageDisposition="SaveOnly" ConflictResolution="AutoResolve"' +
    '          xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">' +
    '      <ItemChanges>' +
    '        <t:ItemChange>' +
    `          <t:ItemId Id="${id}" ChangeKey="${changeKey}" />` +
    '          <t:Updates>' +
    '            <t:SetItemField>' +
    `              <t:ExtendedFieldURI PropertySetId="${appId}" PropertyName="CIHandled" PropertyType="Boolean" />` +
    '              <t:Message>' +
    '                <t:ExtendedProperty>' +
    `                  <t:ExtendedFieldURI PropertySetId="${appId}" PropertyName="CIHandled" PropertyType="Boolean" />` +
    '                  <t:Value>true</t:Value>' +
    '                </t:ExtendedProperty>' +
    '              </t:Message>' +
    '            </t:SetItemField>' +
    '          </t:Updates>' +
    '        </t:ItemChange>' +
    '      </ItemChanges>' +
    '    </UpdateItem>' +
    '  </soap:Body>' +
    '</soap:Envelope>'

  await ewsAsyncMethodWithRetry(() => officeApiMailbox_MakeEwsRequestAsync(xmlRequest))
}

function getEwsResponseValue(xmlDoc: Document, elementName: string, attrName?: string) {
  const elements = xmlDoc.getElementsByTagName(elementName)
  if (elements.length > 0) {
    if (attrName) {
      return elements[0].attributes[attrName].value
    } else {
      return elements[0].innerHTML
    }
  }

  return null
}

function getEwsResponseRecipientList(xmlDoc: Document, elementName: string) {
  const recipients = []

  const elements = xmlDoc.getElementsByTagName(elementName)
  if (elements.length > 0 && elements[0].hasChildNodes()) {
    for (let i = 0; i < elements[0].childNodes.length; i++) {
      const recipient: Office.EmailAddressDetails = {} as Office.EmailAddressDetails
      const mailbox = elements[0].childNodes[i]
      for (let j = 0; j < mailbox.childNodes.length; j++) {
        const mailboxProp = mailbox.childNodes[j]
        switch (mailboxProp.nodeName.toLowerCase()) {
          case 't:name':
            // @ts-ignore
            recipient.displayName = mailboxProp.innerHTML
            break
          case 't:emailaddress':
            // @ts-ignore
            recipient.emailAddress = mailboxProp.innerHTML
            break
        }
      }
      recipients.push(recipient)
    }
  }

  return recipients
}

async function ewsAsyncMethodWithRetry(methodAsync: () => Promise<any>, maxAttempts: number = 50) {
  let attempt = 1
  while (attempt <= maxAttempts) {
    const asyncResult = await methodAsync()
    if (asyncResult.includes('ErrorItemNotFound')) {
      console.log('EWS item not found')
      await new Promise((resolve) => setTimeout(resolve, 500))
      attempt++
    } else {
      return asyncResult
    }
  }
}

const exportFunctions = {
  isAvailable: isEwsApiAvailable,
  url: officeMailEwsUrl,
  token: officeApiMailbox_GetEwsCallbackToken,
  getMailInfo: officeMailEwsInfo,
  getSentItems: getSentItems,
  markItemAsProcessed: markItemAsProcessed,
}

export default exportFunctions
